// src/admin/CatechistManager.js
import React, { useState, useContext, useMemo, useEffect, useRef } from 'react';
import { StudentContext } from '../StudentContext';
import { addCatechistWithAuth, updateCatechist } from '../FirebaseService';
import { firestore } from '../firebaseConfig';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import './CatechistManager.css';

const DEPARTMENT_OPTIONS = ['Ban Kỷ Luật', 'Ban Hành Chánh', 'Ban Phụng Vụ'];

const removeVietnameseTones = (str) => {
  if (!str) return '';
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D');
};

const getHolyNameFirstLetter = (holyName) => {
  if (!holyName) return '';
  const clean = removeVietnameseTones(holyName.trim());
  const letter = clean.charAt(0).toUpperCase();
  return letter.replace(/[^A-Z0-9]/g, '');
};

const getFullNameInitials = (fullName) => {
  if (!fullName) return '';
  const clean = removeVietnameseTones(fullName.trim());
  const words = clean.split(/\s+/).filter(Boolean);
  const initials = words.map((w) => w.charAt(0).toUpperCase()).join('');
  return initials.replace(/[^A-Z0-9]/g, '');
};

const generateCatechistIdCode = (num, holyName, fullName) => {
  const numberStr = String(num).padStart(2, '0');
  const holyInitial = getHolyNameFirstLetter(holyName);
  const nameInitials = getFullNameInitials(fullName);
  return `${numberStr}${holyInitial}${nameInitials}`;
};

const DepartmentMultiSelect = ({ value, onChange, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentDepts = useMemo(() => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' && value.trim()) {
      return value.split(',').map((s) => s.trim()).filter(Boolean);
    }
    return [];
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (dept) => {
    let newDepts;
    if (currentDepts.includes(dept)) {
      newDepts = currentDepts.filter((d) => d !== dept);
    } else {
      newDepts = [...currentDepts, dept];
    }
    onChange(newDepts);
  };

  const displayText = currentDepts.length > 0 ? currentDepts.join(', ') : 'Không';

  return (
    <div className="cm-multi-select-container" ref={dropdownRef}>
      <button
        type="button"
        className="cm-multi-select-trigger"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        <span className="cm-multi-select-text">{displayText}</span>
        <span className="cm-multi-select-arrow">▾</span>
      </button>

      {isOpen && (
        <div className="cm-multi-select-dropdown">
          {DEPARTMENT_OPTIONS.map((dept) => (
            <label key={dept} className="cm-multi-select-option">
              <input
                type="checkbox"
                checked={currentDepts.includes(dept)}
                onChange={() => handleToggle(dept)}
              />
              <span>{dept}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const CatechistManager = () => {
  const { catechistsData, refreshData, updateLocalCatechist } = useContext(StudentContext);

  const [isAddCatechistModalOpen, setIsAddCatechistModalOpen] = useState(false);
  const [nextOrderNum, setNextOrderNum] = useState(1);
  const [isManualId, setIsManualId] = useState(false);

  // Form state có Giới tính & Tu Sĩ
  const [newCatechist, setNewCatechist] = useState({
    idCode: '',
    holyName: '',
    fullName: '',
    gender: 'Nam',
    religious: 'Giáo lý viên',
    username: '',
    password: '',
    role: 'catechist', // Mặc định role là catechist
    className: '',
    blockLeader: '',
    departmentLeader: [],
  });

  const [isAddingCatechist, setIsAddingCatechist] = useState(false);
  const [addCatechistError, setAddCatechistError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isUpdating, setIsUpdating] = useState({});
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const [currentYearClasses, setCurrentYearClasses] = useState([]);
  const [currentYearGroups, setCurrentYearGroups] = useState([]);
  const [currentYearName, setCurrentYearName] = useState('');
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    const fetchCurrentYearData = async () => {
      try {
        setLoadingData(true);
        const yearQuery = query(collection(firestore, 'academic_years'), where('isCurrent', '==', true));
        let yearSnap = await getDocs(yearQuery);
        let currentYearDoc = yearSnap.docs[0];

        if (!currentYearDoc) {
          const allYearsSnap = await getDocs(query(collection(firestore, 'academic_years'), orderBy('startDate', 'desc')));
          if (!allYearsSnap.empty) {
            currentYearDoc = allYearsSnap.docs[0];
          }
        }

        if (!currentYearDoc) {
          setCurrentYearClasses([]);
          setCurrentYearGroups([]);
          setLoadingData(false);
          return;
        }

        const yearId = currentYearDoc.id;
        const yearData = currentYearDoc.data();
        setCurrentYearName(yearData.name || yearId);

        const gradesSnap = await getDocs(collection(firestore, `academic_years/${yearId}/grades`));
        const gradeOrderMap = {};
        const groupOrderMap = new Map();

        gradesSnap.docs.forEach((d) => {
          const g = d.data();
          gradeOrderMap[d.id] = g.sortOrder || 0;

          if (g.group) {
            const currentMinOrder = groupOrderMap.get(g.group) ?? 999;
            if ((g.sortOrder || 0) < currentMinOrder) {
              groupOrderMap.set(g.group, g.sortOrder || 0);
            }
          }
        });

        const sortedGroups = Array.from(groupOrderMap.entries())
          .sort((a, b) => a[1] - b[1])
          .map((entry) => entry[0]);

        setCurrentYearGroups(sortedGroups);

        const classesSnap = await getDocs(collection(firestore, `academic_years/${yearId}/classes`));
        const classesList = classesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        classesList.sort((a, b) => {
          const orderA = gradeOrderMap[a.gradeId] ?? 999;
          const orderB = gradeOrderMap[b.gradeId] ?? 999;
          if (orderA !== orderB) return orderA - orderB;
          return (a.sortOrder || 0) - (b.sortOrder || 0);
        });

        setCurrentYearClasses(classesList);
      } catch (error) {
        console.error('Lỗi khi tải dữ liệu niên khóa từ Firestore:', error);
      } finally {
        setLoadingData(false);
      }
    };

    fetchCurrentYearData();
  }, []);

  const blockLeaderOptions = useMemo(() => {
    const options = [{ value: '', label: 'Không' }];
    currentYearGroups.forEach((groupName) => {
      options.push({ value: groupName, label: groupName });
    });
    return options;
  }, [currentYearGroups]);

  const classOptions = useMemo(() => {
    const options = [{ value: '', label: 'Không phân công' }];
    const addedValues = new Set(['']);

    currentYearClasses.forEach((c) => {
      const nameVal = c.name || c.id;
      if (nameVal && !addedValues.has(nameVal)) {
        options.push({ value: nameVal, label: nameVal });
        addedValues.add(nameVal);
      }
    });

    return options;
  }, [currentYearClasses]);

  const getNextOrderNumber = () => {
    if (!catechistsData || catechistsData.length === 0) return 1;
    let maxNum = 0;
    catechistsData.forEach((c) => {
      if (c.idCode) {
        const match = String(c.idCode).trim().match(/^(\d+)/);
        if (match) {
          const num = parseInt(match[1], 10);
          if (!isNaN(num) && num > maxNum) {
            maxNum = num;
          }
        }
      }
    });
    return maxNum > 0 ? maxNum + 1 : catechistsData.length + 1;
  };

  const handleOpenAddCatechistModal = () => {
    const nextNum = getNextOrderNumber();
    setNextOrderNum(nextNum);
    setIsManualId(false);

    setNewCatechist({
      idCode: String(nextNum).padStart(2, '0'),
      holyName: '',
      fullName: '',
      gender: 'Nam',
      religious: 'Giáo lý viên',
      username: '',
      password: '',
      role: 'catechist',
      className: '',
      blockLeader: '',
      departmentLeader: [],
    });
    setAddCatechistError('');
    setIsAddCatechistModalOpen(true);
  };

  const handleNewCatechistChange = (e) => {
    const { name, value } = e.target;

    if (name === 'idCode') {
      setIsManualId(true);
      setNewCatechist((prev) => ({ ...prev, idCode: value }));
      return;
    }

    setNewCatechist((prev) => {
      const updated = { ...prev, [name]: value };

      if (!isManualId && (name === 'holyName' || name === 'fullName')) {
        const holy = name === 'holyName' ? value : prev.holyName;
        const full = name === 'fullName' ? value : prev.fullName;
        updated.idCode = generateCatechistIdCode(nextOrderNum, holy, full);
      }

      return updated;
    });
  };

  const handleDepartmentCheckboxChangeInModal = (dept) => {
    setNewCatechist((prev) => {
      const current = Array.isArray(prev.departmentLeader)
        ? prev.departmentLeader
        : (prev.departmentLeader ? [prev.departmentLeader] : []);

      const updated = current.includes(dept)
        ? current.filter((d) => d !== dept)
        : [...current, dept];

      return { ...prev, departmentLeader: updated };
    });
  };

  const handleAddNewCatechistSubmit = async (e) => {
    e.preventDefault();
    setAddCatechistError('');

    if (
      !newCatechist.idCode.trim() ||
      !newCatechist.holyName.trim() ||
      !newCatechist.fullName.trim() ||
      !newCatechist.username.trim() ||
      !newCatechist.password.trim()
    ) {
      setAddCatechistError('Vui lòng điền đầy đủ ID Code, Tên Thánh, Họ và Tên, Username và Mật khẩu.');
      return;
    }

    const cleanPayload = {
      ...newCatechist,
      idCode: newCatechist.idCode.trim().replace(/[\s-]/g, '')
    };

    setIsAddingCatechist(true);
    try {
      const response = await addCatechistWithAuth(cleanPayload);
      if (response.success) {
        setIsAddCatechistModalOpen(false);
        alert('Thêm giáo lý viên thành công!');
        if (refreshData) refreshData();
      } else {
        setAddCatechistError(response.message || 'Lỗi không xác định từ server.');
      }
    } catch (error) {
      console.error('Error adding catechist:', error);
      setAddCatechistError(error.message || 'Lỗi khi thêm giáo lý viên. Vui lòng thử lại.');
    } finally {
      setIsAddingCatechist(false);
    }
  };

  const handleUpdateCatechist = async (uid, field, value) => {
    setIsUpdating((prev) => ({ ...prev, [`${uid}-${field}`]: true }));
    try {
      const updates = { [field]: value };
      const response = await updateCatechist(uid, updates);
      if (response.success) {
        updateLocalCatechist(uid, updates);
      } else {
        alert(response.message || 'Lỗi khi cập nhật.');
      }
    } catch (error) {
      console.error(`Error updating catechist ${field}:`, error);
      alert(error.message || 'Lỗi khi cập nhật. Vui lòng thử lại.');
    } finally {
      setIsUpdating((prev) => ({ ...prev, [`${uid}-${field}`]: false }));
    }
  };

  const filteredCatechists = useMemo(() => {
    if (!catechistsData || catechistsData.length === 0) return [];
    const searchLower = searchTerm.toLowerCase();

    return catechistsData.filter((catechist) => {
      if ((catechist.status || 'active') === 'inactive') return false;

      let deptStr = '';
      if (Array.isArray(catechist.departmentLeader)) {
        deptStr = catechist.departmentLeader.join(' ');
      } else if (typeof catechist.departmentLeader === 'string') {
        deptStr = catechist.departmentLeader;
      }

      return (
        (catechist.idCode && catechist.idCode.toLowerCase().includes(searchLower)) ||
        (catechist.holyName && catechist.holyName.toLowerCase().includes(searchLower)) ||
        (catechist.fullName && catechist.fullName.toLowerCase().includes(searchLower)) ||
        (catechist.username && catechist.username.toLowerCase().includes(searchLower)) ||
        (catechist.className && catechist.className.toLowerCase().includes(searchLower)) ||
        (catechist.religious && catechist.religious.toLowerCase().includes(searchLower)) ||
        (catechist.blockLeader && catechist.blockLeader.toLowerCase().includes(searchLower)) ||
        deptStr.toLowerCase().includes(searchLower)
      );
    });
  }, [catechistsData, searchTerm]);

  const sortedCatechists = useMemo(() => {
    if (!sortColumn) return filteredCatechists;

    const getLastName = (name) => {
      if (!name) return '';
      const parts = name.trim().split(/\s+/);
      return parts[parts.length - 1].toLowerCase();
    };

    return [...filteredCatechists].sort((a, b) => {
      let valA = a[sortColumn];
      let valB = b[sortColumn];

      if (sortColumn === 'idCode') {
        return sortDirection === 'asc'
          ? String(valA || '').localeCompare(String(valB || ''), undefined, { numeric: true, sensitivity: 'base' })
          : String(valB || '').localeCompare(String(valA || ''), undefined, { numeric: true, sensitivity: 'base' });
      }

      if (sortColumn === 'fullName') {
        valA = getLastName(a.fullName);
        valB = getLastName(b.fullName);
      } else if (sortColumn === 'religious') {
        valA = a.religious || 'Giáo lý viên';
        valB = b.religious || 'Giáo lý viên';
      } else if (sortColumn === 'departmentLeader') {
        valA = Array.isArray(valA) ? valA.join(', ') : (valA || '');
        valB = Array.isArray(valB) ? valB.join(', ') : (valB || '');
      } else {
        valA = valA || '';
        valB = valB || '';
      }

      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();

      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredCatechists, sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="catechist-manager-container">
      {/* Header */}
      <div className="cm-header">
        <div>
          <h2 className="cm-title">Quản lý Giáo Lý Viên</h2>
          {currentYearName && (
            <p className="cm-year-subtitle">
              ⭐ Áp dụng phân công cho: <strong>{currentYearName}</strong>
            </p>
          )}
        </div>

        <div className="cm-toolbar">
          <input
            type="text"
            placeholder="🔎 Tìm kiếm giáo lý viên..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="cm-search-input"
          />
          <button
            onClick={handleOpenAddCatechistModal}
            className="cm-btn cm-btn-primary"
          >
            + Thêm Giáo Lý Viên
          </button>
        </div>
      </div>

      {/* Modal Thêm Mới */}
      {isAddCatechistModalOpen && (
        <div className="cm-modal-overlay">
          <div className="cm-modal-card">
            <h3 className="cm-modal-title">Thêm Giáo Lý Viên Mới</h3>
            {addCatechistError && <p className="cm-error-text">{addCatechistError}</p>}
            <form onSubmit={handleAddNewCatechistSubmit}>
              <div className="cm-form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <label className="cm-label" style={{ margin: 0 }}>
                    ID Code {!isManualId && <span style={{ color: '#2563eb' }}>(Tự động)</span>}
                  </label>
                  {isManualId && (
                    <button
                      type="button"
                      className="cm-reset-id-btn"
                      onClick={() => {
                        setIsManualId(false);
                        setNewCatechist((prev) => ({
                          ...prev,
                          idCode: generateCatechistIdCode(nextOrderNum, prev.holyName, prev.fullName),
                        }));
                      }}
                    >
                      🔄 Tạo lại tự động
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  name="idCode"
                  value={newCatechist.idCode}
                  onChange={handleNewCatechistChange}
                  placeholder="Ví dụ: 01GNVA"
                  className="cm-input"
                  style={{ fontWeight: 600, color: '#2563eb' }}
                />
                <span style={{ fontSize: '11px', color: '#64748b', marginTop: '3px' }}>
                  Cấu trúc: Số + Chữ đầu Tên Thánh + Chữ đầu Họ Tên
                </span>
              </div>

              <div className="cm-form-group">
                <label className="cm-label">Tên Thánh</label>
                <input
                  type="text"
                  name="holyName"
                  value={newCatechist.holyName}
                  onChange={handleNewCatechistChange}
                  placeholder="Ví dụ: Giuse"
                  className="cm-input"
                />
              </div>

              <div className="cm-form-group">
                <label className="cm-label">Họ và Tên</label>
                <input
                  type="text"
                  name="fullName"
                  value={newCatechist.fullName}
                  onChange={handleNewCatechistChange}
                  placeholder="Ví dụ: Nguyễn Văn An"
                  className="cm-input"
                />
              </div>

              {/* 🌟 THÊM TRƯỜNG GIỚI TÍNH 🌟 */}
              <div className="cm-form-group">
                <label className="cm-label">Giới tính</label>
                <select
                  name="gender"
                  value={newCatechist.gender}
                  onChange={handleNewCatechistChange}
                  className="cm-select"
                >
                  <option value="Nam">Nam</option>
                  <option value="Nữ">Nữ</option>
                </select>
              </div>

              {/* 🌟 THÊM TRƯỜNG TU SĨ 🌟 */}
              <div className="cm-form-group">
                <label className="cm-label">Tu Sĩ / Giáo lý viên</label>
                <select
                  name="religious"
                  value={newCatechist.religious}
                  onChange={handleNewCatechistChange}
                  className="cm-select"
                >
                  <option value="Giáo lý viên">Giáo lý viên</option>
                  <option value="Tu Sĩ">Tu Sĩ</option>
                </select>
              </div>

              <div className="cm-form-group">
                <label className="cm-label">Username</label>
                <input
                  type="text"
                  name="username"
                  value={newCatechist.username}
                  onChange={handleNewCatechistChange}
                  className="cm-input"
                />
              </div>

              <div className="cm-form-group">
                <label className="cm-label">Mật khẩu</label>
                <input
                  type="password"
                  name="password"
                  value={newCatechist.password}
                  onChange={handleNewCatechistChange}
                  className="cm-input"
                />
              </div>

              <div className="cm-form-group">
                <label className="cm-label">Đứng lớp ({currentYearName || 'Hiện tại'})</label>
                <select
                  name="className"
                  value={newCatechist.className}
                  onChange={handleNewCatechistChange}
                  className="cm-select"
                  disabled={loadingData}
                >
                  {classOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className="cm-form-group">
                <label className="cm-label">Trưởng khối / Ngành</label>
                <select
                  name="blockLeader"
                  value={newCatechist.blockLeader}
                  onChange={handleNewCatechistChange}
                  className="cm-select"
                  disabled={loadingData}
                >
                  {blockLeaderOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className="cm-form-group">
                <label className="cm-label">Trưởng ban</label>
                <div className="cm-checkbox-group">
                  {DEPARTMENT_OPTIONS.map((dept) => {
                    const currentDepts = Array.isArray(newCatechist.departmentLeader)
                      ? newCatechist.departmentLeader
                      : (newCatechist.departmentLeader ? [newCatechist.departmentLeader] : []);
                    const isChecked = currentDepts.includes(dept);

                    return (
                      <label key={dept} className="cm-checkbox-label">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleDepartmentCheckboxChangeInModal(dept)}
                        />
                        <span>{dept}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="cm-modal-actions">
                <button
                  type="button"
                  onClick={() => setIsAddCatechistModalOpen(false)}
                  className="cm-btn cm-btn-secondary"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={isAddingCatechist}
                  className="cm-btn cm-btn-primary"
                >
                  {isAddingCatechist ? 'Đang thêm...' : 'Thêm'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="cm-table-wrapper">
        <table className="cm-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('idCode')}>ID Code {getSortIcon('idCode')}</th>
              <th onClick={() => handleSort('holyName')}>Tên Thánh {getSortIcon('holyName')}</th>
              <th onClick={() => handleSort('fullName')} className="cm-th-name">
                Họ và Tên {getSortIcon('fullName')}
              </th>
              <th onClick={() => handleSort('username')}>Username {getSortIcon('username')}</th>
              <th onClick={() => handleSort('className')}>Đứng lớp {getSortIcon('className')}</th>
              {/* 🌟 CỘT TU SĨ THAY THẾ CHO CỘT QUYỀN 🌟 */}
              <th onClick={() => handleSort('religious')}>Tu Sĩ {getSortIcon('religious')}</th>
              <th onClick={() => handleSort('blockLeader')}>Trưởng khối / Ngành {getSortIcon('blockLeader')}</th>
              <th onClick={() => handleSort('departmentLeader')}>Trưởng ban {getSortIcon('departmentLeader')}</th>
            </tr>
          </thead>
          <tbody>
            {sortedCatechists.length > 0 ? (
              sortedCatechists.map((catechist, index) => (
                <tr key={catechist.uid || `glv-${index}-${catechist.username}`} className={index % 2 === 0 ? 'cm-tr-even' : ''}>
                  <td className="cm-td-code">{catechist.idCode}</td>
                  <td>{catechist.holyName}</td>
                  <td className="cm-td-name">{catechist.fullName}</td>
                  <td className="cm-td-username">{catechist.username}</td>
                  <td>
                    <select
                      value={catechist.className || ''}
                      onChange={(e) => handleUpdateCatechist(catechist.uid, 'className', e.target.value)}
                      disabled={isUpdating[`${catechist.uid}-className`] || loadingData}
                      className="cm-select-inline"
                    >
                      {classOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </td>
                  {/* 🌟 CỘT TU SĨ VỚI 2 LỰA CHỌN 🌟 */}
                  <td>
                    <select
                      value={catechist.religious || 'Giáo lý viên'}
                      onChange={(e) => handleUpdateCatechist(catechist.uid, 'religious', e.target.value)}
                      disabled={isUpdating[`${catechist.uid}-religious`]}
                      className="cm-select-inline"
                    >
                      <option value="Giáo lý viên">Giáo lý viên</option>
                      <option value="Tu Sĩ">Tu Sĩ</option>
                    </select>
                  </td>
                  <td>
                    <select
                      value={catechist.blockLeader || ''}
                      onChange={(e) => handleUpdateCatechist(catechist.uid, 'blockLeader', e.target.value)}
                      disabled={isUpdating[`${catechist.uid}-blockLeader`] || loadingData}
                      className="cm-select-inline"
                    >
                      {blockLeaderOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <DepartmentMultiSelect
                      value={catechist.departmentLeader}
                      onChange={(newDepts) => handleUpdateCatechist(catechist.uid, 'departmentLeader', newDepts)}
                      disabled={isUpdating[`${catechist.uid}-departmentLeader`]}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="cm-empty-cell">
                  {searchTerm ? 'Không tìm thấy giáo lý viên nào phù hợp.' : 'Không có dữ liệu giáo lý viên hoặc chưa tải xong.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CatechistManager;